import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import {
  CheckCircle2,
  Users,
  Calendar,
  BarChart3,
  Shield,
  Zap,
  ArrowRight,
  Building2,
  Mail,
  Globe,
  Activity,
  Clock,
  Target,
  ListChecks,
  UserCog,
  Timer,
  FileText,
  Bell,
  UserPlus,
  Layers,
  TrendingUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from "../components/LanguageSwitcher";
import ThemeToggle from "../components/ThemeToggle";

function LandingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // SEO: Set page title and meta based on language
  useEffect(() => {
    const title = i18n.language === 'en' 
      ? "Milestora - Professional Project Management System | Start Free"
      : "Milestora - Profesyonel Proje Yönetim Sistemi | Ücretsiz Başlayın";
    document.title = title;
  }, [i18n.language]);

  const features = [
    {
      icon: <Users className="h-6 w-6" />,
      titleKey: "multiUserManagement",
      descKey: "multiUserManagementDesc"
    },
    {
      icon: <Calendar className="h-6 w-6" />,
      titleKey: "multipleViews",
      descKey: "multipleViewsDesc"
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      titleKey: "reportingAnalytics",
      descKey: "reportingAnalyticsDesc"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      titleKey: "taskManagement",
      descKey: "taskManagementDesc"
    },
    {
      icon: <Zap className="h-6 w-6" />,
      titleKey: "timesheetTracking",
      descKey: "timesheetTrackingDesc"
    },
    {
      icon: <Target className="h-6 w-6" />,
      titleKey: "teamCollaboration",
      descKey: "teamCollaborationDesc"
    }
  ];

  const stats = [
    { number: "1000+", labelKey: "activeUsers", icon: <Activity className="h-5 w-5" /> },
    { number: "500+", labelKey: "completedProjects", icon: <BarChart3 className="h-5 w-5" /> },
    { number: "98%", labelKey: "teamSatisfaction", icon: <Users className="h-5 w-5" /> },
    { number: "99.9%", labelKey: "uptimeRate", icon: <Clock className="h-5 w-5" /> }
  ];

  const pricingPlans = [
    {
      nameKey: "planFree",
      price: "0",
      periodKey: "planFreePeriod",
      descKey: "planFreeDesc",
      featureKeys: [
        "feature5Users",
        "feature3Projects",
        "featureBasicTasks",
        "featureKanban",
        "featureEmailNotifications",
        "featureCommunitySupport"
      ],
      popular: false,
      ctaKey: "ctaStartFree"
    },
    {
      nameKey: "planProfessional",
      price: "299",
      periodKey: "planProfessionalPeriod",
      descKey: "planProfessionalDesc",
      featureKeys: [
        "featureUnlimitedUsers",
        "featureUnlimitedProjects",
        "featureAllViews",
        "featureGantt",
        "featureActivityLog",
        "featureActiveUsers",
        "featureAdvancedReporting",
        "featureExcelExport",
        "featurePrioritySupport"
      ],
      popular: true,
      ctaKey: "ctaStartNow"
    },
    {
      nameKey: "planEnterprise",
      priceKey: "priceCustom",
      periodKey: "planEnterprisePeriod",
      descKey: "planEnterpriseDesc",
      featureKeys: [
        "featureAllProfessional",
        "featureSelfHosted",
        "featureCustomDomain",
        "featureAPIAccess",
        "featureCustomIntegrations",
        "featureDedicatedServer",
        "featureSLA",
        "featureCustomTraining",
        "featureTechnicalSupport"
      ],
      popular: false,
      ctaKey: "ctaContactUs"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header/Navbar */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg p-1.5 sm:p-2">
                <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <span className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                Milestora
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeToggle />
              <LanguageSwitcher />
              <Button
                variant="ghost"
                className="hidden md:inline-flex"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t('landing.features')}
              </Button>
              <Button
                variant="ghost"
                className="hidden md:inline-flex"
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t('landing.pricing')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-sm sm:text-base"
                onClick={() => navigate("/login")}
              >
                {t('landing.login')}
              </Button>
              <Button
                size="sm"
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-sm sm:text-base"
                onClick={() => navigate("/register")}
              >
                <span className="hidden sm:inline">{t('landing.startFree')}</span>
                <span className="sm:hidden">{t('landing.start')}</span>
                <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-12 sm:pt-20 pb-16 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-100/50 to-blue-100/50" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob" />
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
              {t('landing.cloudBadge')}
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              {t('landing.heroTitle')}
              <span className="block bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mt-1 sm:mt-2">
                {t('landing.heroSubtitle')}
              </span>
            </h1>
            <p className="text-base sm:text-xl md:text-2xl text-gray-600 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
              {t('landing.heroDescription')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
                onClick={() => navigate("/register")}
              >
                <Zap className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                {t('landing.startFreeButton')}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t('landing.discoverFeatures')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg mb-2 sm:mb-4">
                  <div className="text-purple-600">{stat.icon}</div>
                </div>
                <div className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-1 sm:mb-2">
                  {stat.number}
                </div>
                <div className="text-xs sm:text-base text-gray-600">{t(`landing.${stat.labelKey}`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-12 sm:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('landing.featuresTitle')}
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              {t('landing.featuresDescription')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-purple-200 hover:shadow-xl transition-all duration-300 group">
                <CardContent className="p-4 sm:p-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                    <div className="text-purple-600">{feature.icon}</div>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{t(`landing.${feature.titleKey}`)}</h3>
                  <p className="text-sm sm:text-base text-gray-600">{t(`landing.${feature.descKey}`)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Features Section */}
      <section id="detailed-features" className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('landing.detailedFeaturesTitle')}
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              {t('landing.detailedFeaturesDescription')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Project & Task Management */}
            <Card className="border-2 border-purple-100 hover:border-purple-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                    <ListChecks className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 ml-4">{t('landing.projectTaskManagementTitle')}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{t('landing.projectTaskManagementDesc')}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">{t('landing.badgeSubtasks')}</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">{t('landing.badgeDependencies')}</span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">{t('landing.badgeChecklist')}</span>
                </div>
              </CardContent>
            </Card>

            {/* Role-Based Access Control */}
            <Card className="border-2 border-purple-100 hover:border-purple-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                    <UserCog className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 ml-4">{t('landing.roleBasedAccessTitle')}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{t('landing.roleBasedAccessDesc')}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">{t('landing.badgeSupervisor')}</span>
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">{t('landing.badgeAdmin')}</span>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">{t('landing.badgeManager')}</span>
                </div>
              </CardContent>
            </Card>

            {/* Timesheet Management */}
            <Card className="border-2 border-purple-100 hover:border-purple-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                    <Timer className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 ml-4">{t('landing.timesheetManagementTitle')}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{t('landing.timesheetManagementDesc')}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">{t('landing.badgeWeeklyView')}</span>
                  <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">{t('landing.badgeWorkType')}</span>
                </div>
              </CardContent>
            </Card>

            {/* Reporting & Analytics */}
            <Card className="border-2 border-purple-100 hover:border-purple-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 ml-4">{t('landing.reportingTitle')}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{t('landing.reportingDesc')}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">{t('landing.badgeExcelExport')}</span>
                  <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">{t('landing.badgeCharts')}</span>
                </div>
              </CardContent>
            </Card>

            {/* Activity Logging */}
            <Card className="border-2 border-purple-100 hover:border-purple-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg flex items-center justify-center">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 ml-4">{t('landing.activityLogTitle')}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{t('landing.activityLogDesc')}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">{t('landing.badgeAuditTrail')}</span>
                  <span className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-sm">{t('landing.badgeSecurity')}</span>
                </div>
              </CardContent>
            </Card>

            {/* Notification System */}
            <Card className="border-2 border-purple-100 hover:border-purple-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-lg flex items-center justify-center">
                    <Bell className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 ml-4">{t('landing.notificationSystemTitle')}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{t('landing.notificationSystemDesc')}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">{t('landing.badgeEmail')}</span>
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">{t('landing.badgeCustomizable')}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section id="screenshots" className="py-12 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('landing.screenshotsTitle')}
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              {t('landing.screenshotsDescription')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Hero Image */}
            <Card className="overflow-hidden border-2 border-gray-200 hover:border-purple-300 hover:shadow-2xl transition-all duration-300 lg:col-span-2">
              <CardContent className="p-0">
                <div className="relative group">
                  <img 
                    src="https://images.unsplash.com/photo-1545063328-c8e3faffa16f?w=1200&h=600&fit=crop" 
                    alt="Milestora Dashboard"
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                    <div className="text-white">
                      <h3 className="text-2xl font-bold mb-2">{t('landing.dashboardTitle')}</h3>
                      <p className="text-gray-200">{t('landing.dashboardDesc')}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Team Management Image */}
            <Card className="overflow-hidden border-2 border-gray-200 hover:border-purple-300 hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-0">
                <div className="relative group">
                  <img 
                    src="https://images.unsplash.com/photo-1580983558189-84200466afb8?w=600&h=400&fit=crop" 
                    alt="Team Management"
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                    <div className="text-white">
                      <h3 className="text-xl font-bold mb-2">{t('landing.roleBasedAccessTitle')}</h3>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Analytics Image */}
            <Card className="overflow-hidden border-2 border-gray-200 hover:border-purple-300 hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-0">
                <div className="relative group">
                  <img 
                    src="https://images.unsplash.com/photo-1477013743164-ffc3a5e556da?w=600&h=400&fit=crop" 
                    alt="Analytics Dashboard"
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                    <div className="text-white">
                      <h3 className="text-xl font-bold mb-2">{t('landing.reportingTitle')}</h3>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Time Tracking Image */}
            <Card className="overflow-hidden border-2 border-gray-200 hover:border-purple-300 hover:shadow-2xl transition-all duration-300 lg:col-span-2">
              <CardContent className="p-0">
                <div className="relative group">
                  <img 
                    src="https://images.unsplash.com/photo-1591508240937-7bbf3674d427?w=1200&h=600&fit=crop" 
                    alt="Time Tracking"
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                    <div className="text-white">
                      <h3 className="text-2xl font-bold mb-2">{t('landing.timesheetManagementTitle')}</h3>
                      <p className="text-gray-200">{t('landing.timesheetManagementDesc')}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.pricingTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('landing.pricingDescription')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative border-2 ${
                  plan.popular 
                    ? 'border-purple-500 shadow-2xl scale-105' 
                    : 'border-gray-200'
                } hover:shadow-xl transition-all duration-300`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-1 rounded-full text-sm font-semibold">
                      {t('landing.mostPopular')}
                    </span>
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t(`landing.${plan.nameKey}`)}</h3>
                  <p className="text-gray-600 mb-6">{t(`landing.${plan.descKey}`)}</p>
                  
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      {plan.priceKey ? (
                        <span className="text-4xl font-bold text-gray-900">{t(`landing.${plan.priceKey}`)}</span>
                      ) : (
                        <>
                          <span className="text-5xl font-bold text-gray-900">₺{plan.price}</span>
                          <span className="text-gray-600">/ {t(`landing.${plan.periodKey}`)}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <Button
                    className={`w-full mb-6 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                    onClick={() => {
                      if (plan.nameKey === "planEnterprise") {
                        window.location.href = "mailto:milestora@enerotomasyon.com.tr?subject=Kurumsal Plan Teklifi";
                      } else {
                        navigate("/register");
                      }
                    }}
                  >
                    {t(`landing.${plan.ctaKey}`)}
                  </Button>

                  <ul className="space-y-3">
                    {plan.featureKeys.map((featureKey, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{t(`landing.${featureKey}`)}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              {t('landing.pricingNote')}
            </p>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => {
                window.location.href = "mailto:milestora@enerotomasyon.com.tr?subject=Kurumsal Plan Teklifi";
              }}
            >
              <Mail className="mr-2 h-5 w-5" />
              {t('landing.customQuote')}
            </Button>
          </div>
        </div>
      </section>

      {/* Company Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-12 text-white">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Building2 className="h-8 w-8" />
                  <h2 className="text-3xl font-bold">{t('landing.companyTitle')}</h2>
                </div>
                <h3 className="text-2xl mb-4 opacity-90">
                  Elektronik Bilişim Güvenlik Teknolojileri San. Tic. Ltd. Şti.
                </h3>
                <p className="text-lg opacity-80 mb-8">
                  {t('landing.companyDescription')}
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Globe className="h-5 w-5" />
                    <a href="http://www.milestora.com" className="hover:underline">
                      www.milestora.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5" />
                    <a href="mailto:milestora@enerotomasyon.com.tr" className="hover:underline">
                      milestora@enerotomasyon.com.tr
                    </a>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
                <h4 className="text-2xl font-bold mb-6">{t('landing.whyMilestora')}</h4>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span>{t('landing.openSourceSelfHosted')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span>{t('landing.activityLogging')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span>{t('landing.multiTenantArchitecture')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span>{t('landing.roleLevels')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" />
                    <span>{t('landing.undoMechanism')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <Clock className="h-16 w-16 mx-auto mb-6 text-purple-600" />
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('landing.trialTitle')}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('landing.trialDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-lg px-12 py-6"
              onClick={() => navigate("/register")}
            >
              <Zap className="mr-2 h-5 w-5" />
              {t('landing.startFreeNow')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-12 py-6"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('landing.viewPricing')}
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section - SEO Optimized */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('landing.faqTitle')}
            </h2>
            <p className="text-base sm:text-xl text-gray-600">
              {t('landing.faqDescription')}
            </p>
          </div>

          <div className="space-y-4" itemScope itemType="https://schema.org/FAQPage">
            <details className="bg-gray-50 rounded-lg p-4 sm:p-6 group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <summary className="font-semibold text-base sm:text-lg cursor-pointer list-none flex items-center justify-between" itemProp="name">
                {t('landing.faqQuestion1')}
                <span className="ml-2 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="mt-3 text-sm sm:text-base text-gray-600" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p itemProp="text">{t('landing.faqAnswer1')}</p>
              </div>
            </details>

            <details className="bg-gray-50 rounded-lg p-4 sm:p-6 group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <summary className="font-semibold text-base sm:text-lg cursor-pointer list-none flex items-center justify-between" itemProp="name">
                {t('landing.faqQuestion2')}
                <span className="ml-2 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="mt-3 text-sm sm:text-base text-gray-600" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p itemProp="text">{t('landing.faqAnswer2')}. 5 kullanıcıya kadar, 3 projeye kadar ücretsiz erişim sağlar. İhtiyacınıza göre ücretli planlara geçiş yapabilirsiniz.</p>
              </div>
            </details>

            <details className="bg-gray-50 rounded-lg p-4 sm:p-6 group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <summary className="font-semibold text-base sm:text-lg cursor-pointer list-none flex items-center justify-between" itemProp="name">
                {t('landing.faqQuestion3')}
                <span className="ml-2 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="mt-3 text-sm sm:text-base text-gray-600" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p itemProp="text">{t('landing.faqAnswer3')}</p>
              </div>
            </details>

            <details className="bg-gray-50 rounded-lg p-4 sm:p-6 group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <summary className="font-semibold text-base sm:text-lg cursor-pointer list-none flex items-center justify-between" itemProp="name">
                {t('landing.faqQuestion4')}
                <span className="ml-2 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="mt-3 text-sm sm:text-base text-gray-600" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p itemProp="text">{t('landing.faqAnswer4')}</p>
              </div>
            </details>

            <details className="bg-gray-50 rounded-lg p-4 sm:p-6 group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <summary className="font-semibold text-base sm:text-lg cursor-pointer list-none flex items-center justify-between" itemProp="name">
                {t('landing.faqQuestion5')}
                <span className="ml-2 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="mt-3 text-sm sm:text-base text-gray-600" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p itemProp="text">{t('landing.faqAnswer5')}</p>
              </div>
            </details>

            <details className="bg-gray-50 rounded-lg p-4 sm:p-6 group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <summary className="font-semibold text-base sm:text-lg cursor-pointer list-none flex items-center justify-between" itemProp="name">
                {t('landing.faqQuestion6')}
                <span className="ml-2 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="mt-3 text-sm sm:text-base text-gray-600" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p itemProp="text">{t('landing.faqAnswer6')}</p>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg p-2">
                  <CheckCircle2 className="h-6 w-6 text-white" />
                </div>
                <span className="text-2xl font-bold">Milestora</span>
              </div>
              <p className="text-gray-400">
                {t('landing.footerDescription')}
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('landing.quickLinks')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button onClick={() => navigate("/register")} className="hover:text-white transition-colors">
                    {t('landing.startFree')}
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate("/login")} className="hover:text-white transition-colors">
                    {t('landing.login')}
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('landing.contact')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  <a href="http://www.milestora.com" className="hover:text-white transition-colors">
                    www.milestora.com
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:milestora@enerotomasyon.com.tr" className="hover:text-white transition-colors">
                    milestora@enerotomasyon.com.tr
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>{t('landing.footerCopyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
